import { supabase } from '../lib/supabase';
import { LogEntry, LogFilter } from '../types/database';

class LogService {
  /**
   * Query historical logs with filters
   */
  async queryLogs(filter: LogFilter): Promise<LogEntry[]> {
    let query = supabase
      .schema('operations')
      .from('logs')
      .select('*')
      .order('timestamp', { ascending: false });

    if (filter.pcInstanceId) {
      query = query.eq('pc_instance_id', filter.pcInstanceId);
    }
    if (filter.networkId) {
      query = query.eq('network_id', filter.networkId);
    }
    if (filter.roomId) {
      query = query.eq('room_id', filter.roomId);
    }
    if (filter.appName) {
      query = query.eq('app_name', filter.appName);
    }
    if (filter.category) {
      query = query.eq('category', filter.category);
    }
    if (filter.level) {
      query = query.eq('level', filter.level);
    }
    if (filter.search) {
      query = query.textSearch('message', filter.search);
    }
    if (filter.startTime) {
      query = query.gte('timestamp', filter.startTime);
    }
    if (filter.endTime) {
      query = query.lte('timestamp', filter.endTime);
    }

    const limit = filter.limit || 100;
    const offset = filter.offset || 0;
    query = query.range(offset, offset + limit - 1);

    const { data, error } = await query;

    if (error) {
      console.error('Failed to query logs:', error);
      return [];
    }

    return data as LogEntry[];
  }

  /**
   * Subscribe to real-time log inserts. Returns an unsubscribe function.
   * Supabase Realtime only supports one column filter per subscription,
   * so we filter by room_id (broadest useful scope) and filter the rest client-side.
   */
  subscribeToLogs(
    filter: LogFilter,
    onNewLog: (log: LogEntry) => void
  ): () => void {
    let realtimeFilter: string | undefined;
    if (filter.pcInstanceId) {
      realtimeFilter = `pc_instance_id=eq.${filter.pcInstanceId}`;
    } else if (filter.roomId) {
      realtimeFilter = `room_id=eq.${filter.roomId}`;
    } else if (filter.networkId) {
      realtimeFilter = `network_id=eq.${filter.networkId}`;
    }

    const channel = supabase
      .channel('logs-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'operations',
          table: 'logs',
          filter: realtimeFilter,
        },
        (payload) => {
          const log = payload.new as LogEntry;

          // Client-side filtering for fields not covered by the realtime filter
          if (filter.appName && log.app_name !== filter.appName) return;
          if (filter.category && log.category !== filter.category) return;
          if (filter.level && log.level !== filter.level) return;

          onNewLog(log);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }

  /**
   * Send a log from the operator frontend directly to Supabase
   */
  async sendLog(
    level: string,
    message: string,
    metadata?: Record<string, any>,
    roomId?: string
  ): Promise<void> {
    const { error } = await supabase
      .schema('operations')
      .from('logs')
      .insert({
        app_name: 'reality_operator',
        level,
        message,
        metadata: metadata || null,
        room_id: roomId || null,
        pc_instance_id: null,
        network_id: null,
        timestamp: new Date().toISOString(),
      });

    if (error) {
      console.error('Failed to send log:', error);
    }
  }
}

export const logService = new LogService();
